import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { notificationService } from '@/services/notification.service';
import { ticketsService } from '@/services/tickets.service';
import { IBulkTicketFormData } from '@/interfaces/tickets/IBulkTicketFormData';
import { useSubscriptionCheck } from '@/hooks/useSubscriptionCheck';
import { SubscriptionModal } from '@/components/SubscriptionModal';
import { convertToPng } from '@/utils/image-converter';

// Create a simple placeholder image for unreleased tickets
function createPlaceholderFile(): File {
  const canvas = document.createElement('canvas');
  canvas.width = 400;
  canvas.height = 300;
  const ctx = canvas.getContext('2d');
  
  if (ctx) {
    // Gray background
    ctx.fillStyle = '#f3f4f6';
    ctx.fillRect(0, 0, 400, 300);
    
    // Border
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 4;
    ctx.strokeRect(10, 10, 380, 280);
    
    // "UNRELEASED" text
    ctx.fillStyle = '#6b7280';
    ctx.font = 'bold 32px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('UNRELEASED', 200, 150);
    
    // Subtitle
    ctx.font = '14px Arial, sans-serif';
    ctx.fillStyle = '#9ca3af';
    ctx.fillText('Ticket will be uploaded later', 200, 180);
  }
  
  // Convert to blob
  const dataUrl = canvas.toDataURL('image/png');
  const byteString = atob(dataUrl.split(',')[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  const blob = new Blob([ab], { type: 'image/png' });
  return new File([blob], 'unreleased-ticket-placeholder.png', { type: 'image/png' });
}

export const CreateBulkTickets = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<IBulkTicketFormData>({
    eventName: '',
    price: 0,
    emails: [],
    file: null,
    numberOfTickets: 0,
    recipient_note: ''
  });
  const [isUnreleased, setIsUnreleased] = useState(false);
  const [skipSubscriptionCheck, setSkipSubscriptionCheck] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isModalOpen, setIsModalOpen, isCheckingSubscription, checkSubscription } = useSubscriptionCheck();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const paymentSuccess = urlParams.get('payment_success') === 'true';
    console.log('paymentSuccess', paymentSuccess);
    
    if (paymentSuccess) {
      setSkipSubscriptionCheck(true);
      
      notificationService.success('Subscription successfully activated');
    } 
  }, [location.search, navigate]);

  useEffect(() => {
    if (!skipSubscriptionCheck) {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      checkSubscription(() => {});
    }
  }, [skipSubscriptionCheck, checkSubscription]);

  const handleModalClose = () => {
    setIsModalOpen(false);
    navigate('/tickets');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        file: file
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isUnreleased && !formData.file) {
      notificationService.error('Please select a file or mark as unreleased');
      return;
    }
    if (formData.price < 0) {
      notificationService.error('Price must be greater than or equal to 0');
      return;
    }
    if (formData.numberOfTickets <= 0) {
      notificationService.error('Number of tickets must be greater than 0');
      return;
    }

    setIsLoading(true);
    try {
      // If unreleased, send one placeholder image per ticket
      if (isUnreleased) {
        // Create tickets one at a time with placeholder images
        for (let i = 0; i < formData.numberOfTickets; i++) {
          const placeholderFile = createPlaceholderFile();
          const formDataToSend = new FormData();
          formDataToSend.append('eventName', formData.eventName);
          formDataToSend.append('price', formData.price.toString());
          formDataToSend.append('numberOfTickets', '1'); // One ticket per request
          formDataToSend.append('emails', formData.emails[i] || '');
          formDataToSend.append('recipient_note', formData.recipient_note);
          formDataToSend.append('emmiter_note', '');
          formDataToSend.append('files', placeholderFile);
          
          await ticketsService.createBulkTickets(formDataToSend);
        }
      } else if (formData.file) {
        // Process file normally
        let filesToSend: File[] = [];
        
        try {
          const convertedFiles = await convertToPng(formData.file);
          
          if (Array.isArray(convertedFiles)) {
            filesToSend = convertedFiles;
          } else {
            filesToSend = [convertedFiles];
          }
        } catch (conversionError) {
          console.error('Error converting file:', conversionError);
          notificationService.error('Could not convert the file, using original format');
          filesToSend = [formData.file];
        }

        for (const fileToSend of filesToSend) {
          const formDataToSend = new FormData();
          formDataToSend.append('eventName', formData.eventName);
          formDataToSend.append('price', formData.price.toString());
          formDataToSend.append('numberOfTickets', formData.numberOfTickets.toString());
          formDataToSend.append('emails', formData.emails.join(','));
          formDataToSend.append('recipient_note', formData.recipient_note);
          formDataToSend.append('emmiter_note', ''); // Required by old backend until redeployed
          formDataToSend.append('files', fileToSend);

          await ticketsService.createBulkTickets(formDataToSend);
        }
      }
      
      notificationService.success(`Tickets created successfully.`);
      navigate('/tickets');
    } catch (error) {
      if (error instanceof Error) {
        notificationService.error(error.message);
      } else {
        notificationService.error('Error creating bulk tickets');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isCheckingSubscription) {
    return (
      <div className="flex justify-center items-center h-64" data-testid="loading-message">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F9015]"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-4xl font-bold text-[#0F9015] mb-8" data-testid="page-title">Upload Tickets</h1>
        <div className="bg-white rounded-lg shadow-md p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div>
              <label className="block text-lg font-semibold text-[#084509] mb-2">
                Event Name
              </label>
              <input
                type="text"
                required
                value={formData.eventName}
                onChange={(e) => setFormData(prev => ({ ...prev, eventName: e.target.value }))}
                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F9015] text-black font-semibold placeholder:text-gray-400 placeholder:font-normal"
                placeholder="Enter event name"
                data-testid="event-name-input"
              />
            </div>

            <div>
              <label className="block text-lg font-semibold text-[#084509] mb-2">
                Price
              </label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) }))}
                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F9015] text-black font-semibold placeholder:text-gray-400 placeholder:font-normal"
                placeholder="Enter ticket price"
                data-testid="price-input"
              />
            </div>

            {/* Email field hidden - emails can be added on Edit Ticket page */}

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isUnreleased}
                  onChange={(e) => {
                    setIsUnreleased(e.target.checked);
                    if (e.target.checked) {
                      setFormData(prev => ({ ...prev, file: null }));
                    }
                  }}
                  className="mt-1 w-5 h-5 text-[#0F9015] border-gray-300 rounded focus:ring-[#0F9015]"
                  data-testid="unreleased-checkbox"
                />
                <div>
                  <span className="text-lg font-semibold text-[#084509]">
                    Mark as Unreleased
                  </span>
                  <p className="text-sm text-gray-600 mt-1">
                    Check this if the ticket file hasn't been released yet. You can upload the file later via Edit Ticket.
                  </p>
                </div>
              </label>
            </div>

            {!isUnreleased && (
              <div>
                <label className="block text-lg font-semibold text-[#084509] mb-2">
                  File
                  <span className="ml-2 text-sm font-normal text-gray-600">
                    ( Choose a Single or Multipage PDF, or a Single Image File to be uploaded )
                  </span>
                </label>
                <input
                  type="file"
                  accept=".pdf,image/*"
                  onChange={handleFileChange}
                  className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F9015] text-black font-semibold file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-lg file:font-semibold file:bg-[#C9FD05] file:text-black hover:file:bg-[#b4e404]"
                  data-testid="file-input"
                />
              </div>
            )}

            <div>
              <label className="block text-lg font-semibold text-[#084509] mb-2">
                Number of Tickets
              </label>
              <input
                type="number"
                required
                value={formData.numberOfTickets}
                onChange={(e) => setFormData(prev => ({ ...prev, numberOfTickets: parseInt(e.target.value) }))}
                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F9015] text-black font-semibold"
                placeholder="Enter number of tickets to create"
                data-testid="number-of-tickets-input"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-6 py-4 text-xl bg-[#C9FD05] text-black font-bold rounded-lg hover:bg-[#b4e404] focus:outline-none focus:ring-2 focus:ring-[#0F9015] shadow-md disabled:opacity-50 disabled:cursor-not-allowed mt-4 flex items-center justify-center"
              data-testid="submit-button"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-black mr-2"></div>
                  Creating Bulk Tickets...
                </>
              ) : (
                'Upload Tickets'
              )}
            </button>
            <p className="text-sm text-gray-500 mt-2 text-center">
              This process may take several seconds as it processes images and creates tickets
            </p>
          </form>
        </div>
      </div>

      {isModalOpen && !skipSubscriptionCheck && (
        <SubscriptionModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSubscribed={() => setIsModalOpen(false)}
          price={29.99}
        />
      )}

    </div>
  );
}; 